import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CreditPurchaseModal from './CreditPurchaseModal';
import './UsageDashboard.css';

const UsageDashboard = ({ user, stats: serverStats = null }) => {
  const { t } = useTranslation();
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [period, setPeriod] = useState('30d');

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Temporary function to handle upgrade requests
  const handleUpgradeRequest = () => {
    alert(t('usageDashboard.upgradeAlert'));
  };

  useEffect(() => {
    // If server provided aggregated stats, use them as a lightweight seed
    if (serverStats) {
      setUsageData(serverStats);
      setLoading(false);
      return;
    }

    loadUsageData();
    // Auto-refresh toutes les 60 secondes
    const interval = setInterval(loadUsageData, 60000);
    return () => clearInterval(interval);
  }, [period, serverStats]);

  const loadUsageData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/usage/current`, {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUsageData(data);
      }
    } catch (error) {
      console.error(t('usageDashboard.errorLoadingUsage'), error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseSuccess = () => {
    setTimeout(loadUsageData, 1000);
  };

  if (loading && !usageData) {
    return (
      <div className="usage-dashboard">
        <div className="usage-dashboard-header">
          <h2>📊 {t('usageDashboard.title')}</h2>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>{t('usageDashboard.loadingData')}</span>
        </div>
      </div>
    );
  }

  if (!usageData) {
    return (
      <div className="usage-dashboard">
        <div className="usage-dashboard-header">
          <h2>📊 {t('usageDashboard.title')}</h2>
        </div>
        <div className="error-message">
          ❌ {t('usageDashboard.cannotLoadData')}
        </div>
      </div>
    );
  }

  // If usageData looks like a default/uninitialized super-admin license (huge limits and zero usage), show hint
  const isLikelyDefaultUnlimited = () => {
    try {
      const l = usage.license || license;
      if (!l) return false;
      const limits = usageData.license ? usageData.license : null;
      // fallback detection using usage & raw numbers
      const hugeThreshold = 99999999;
      const veryLarge = (val) => typeof val === 'number' && val >= hugeThreshold;
      if (veryLarge(usage.apiCalls.limit) && usage.apiCalls.used === 0 && usage.users.used <= 1) return true;
      return false;
    } catch (e) {
      return false;
    }
  };

  const showInitHint = isLikelyDefaultUnlimited();

  const { usage, license, alerts } = usageData;

  // Helper to format limits: treat very large numbers as 'Unlimited'
  const formatLimit = (value) => {
    if (value === undefined || value === null) return '—';
    if (typeof value === 'number' && value >= 99999999) return 'Illimité';
    return value;
  };

  const formatStorage = (value) => {
    if (value === undefined || value === null) return '—';
    if (typeof value === 'number' && value >= 99999999) return 'Illimité';
    return typeof value === 'number' ? `${value.toFixed(2)} GB` : value;
  };

  return (
    <div className="usage-dashboard">
      <div className="usage-dashboard-header">
        <h2>📊 {t('usageDashboard.title')}</h2>
        <div className="header-actions">
          <button 
            className="refresh-dashboard-btn"
            onClick={loadUsageData}
            disabled={loading}
          >
            {loading ? '⏳' : '🔄'} {t('usageDashboard.refresh')}
          </button>
          <span className="last-update">
            {t('usageDashboard.lastUpdate')}: {new Date(usageData.refreshedAt).toLocaleTimeString()}
          </span>
        </div>
      </div>

      {showInitHint && (
        <div className="usage-init-hint">
          ⚠️ Données d'usage vides détectées (licence globale illimitée). Si vous êtes en développement, exécutez le script d'initialisation des licences ou seedez des données pour voir les métriques réelles.
        </div>
      )}

      {/* Alertes globales */}
      {alerts && alerts.length > 0 && (
        <div className="global-alerts">
          {alerts.map((alert, index) => (
            <div key={index} className={`global-alert ${alert.level}`}>
              <div className="alert-content">
                <span className="alert-icon">
                  {alert.level === 'critical' ? '🚨' : '⚠️'}
                </span>
                <div className="alert-text">
                  <strong>{alert.message}</strong>
                  {alert.action === 'buy_credits' && (
                    <button 
                      className="alert-action-btn"
                      onClick={() => setShowCreditModal(true)}
                    >
                      💳 {t('usageDashboard.buyCredits')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Licence info */}
      <div className="license-info-card">
        <div className="license-info-header">
          <h3>🎫 {t('usageDashboard.licenseInfo')}</h3>
          <span className={`license-status-badge ${license.status}`}>
            {license.status.toUpperCase()}
          </span>
        </div>
        <div className="license-info-content">
          <div className="license-info-item">
            <span className="info-label">{t('usageDashboard.plan')}</span>
            <span className="info-value">{license.plan} ({license.type})</span>
          </div>
          <div className="license-info-item">
            <span className="info-label">{t('usageDashboard.expiresOn')}</span>
            <span className="info-value">
              {new Date(license.endDate).toLocaleDateString()}
              {license.daysRemaining && (
                <small> ({license.daysRemaining} {t('usageDashboard.daysRemaining')})</small>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Usage overview */}
      <div className="usage-overview">
        <h3>📈 {t('usageDashboard.usageOverview')}</h3>
        <div className="usage-cards-grid">
          {/* API Calls */}
          <div className="usage-card api-calls">
            <div className="usage-card-header">
              <div className="usage-card-icon">🔌</div>
              <div className="usage-card-title">
                <h4>API Calls</h4>
                <span className="usage-card-subtitle">{t('usageDashboard.apiCalls')}</span>
              </div>
              <div className={`usage-card-percentage ${
                usage.apiCalls.percentage >= 90 ? 'critical' : 
                usage.apiCalls.percentage >= 80 ? 'warning' : 'normal'
              }`}>
                {usage.apiCalls.percentage}%
              </div>
            </div>
            
            <div className="usage-card-progress">
              <div className="usage-progress-bar">
                <div 
                  className={`usage-progress-fill ${
                    usage.apiCalls.percentage >= 90 ? 'critical' : 
                    usage.apiCalls.percentage >= 80 ? 'warning' : 'normal'
                  }`}
                  style={{ width: `${Math.min(100, usage.apiCalls.percentage)}%` }}
                ></div>
              </div>
              <div className="usage-numbers">
                <span className="used">{usage.apiCalls.used.toLocaleString()}</span>
                <span className="separator">/</span>
                <span className="limit">{formatLimit(usage.apiCalls.limit)}</span>
              </div>
            </div>

            <div className="usage-card-footer">
              <span className="remaining">
                {usage.apiCalls.remaining.toLocaleString()} {t('usageDashboard.remaining')}
              </span>
              {usage.apiCalls.percentage >= 80 && (
                <button 
                  className="buy-more-btn"
                  onClick={() => setShowCreditModal(true)}
                >
                  💳 {t('usageDashboard.buyMore')}
                </button>
              )}
            </div>
          </div>

          {/* Nœuds */}
          <div className="usage-card nodes">
            <div className="usage-card-header">
              <div className="usage-card-icon">🔗</div>
              <div className="usage-card-title">
                <h4>{t('usageDashboard.nodes')}</h4>
                <span className="usage-card-subtitle">{t('usageDashboard.createdNodes')}</span>
              </div>
              <div className={`usage-card-percentage ${
                usage.nodes.percentage >= 90 ? 'critical' : 
                usage.nodes.percentage >= 80 ? 'warning' : 'normal'
              }`}>
                {usage.nodes.percentage}%
              </div>
            </div>
            
            <div className="usage-card-progress">
              <div className="usage-progress-bar">
                <div 
                  className={`usage-progress-fill ${
                    usage.nodes.percentage >= 90 ? 'critical' : 
                    usage.nodes.percentage >= 80 ? 'warning' : 'normal'
                  }`}
                  style={{ width: `${Math.min(100, usage.nodes.percentage)}%` }}
                ></div>
              </div>
            <div className="usage-numbers">
                <span className="used">{usage.nodes.used.toLocaleString()}</span>
                <span className="separator">/</span>
                <span className="limit">{formatLimit(usage.nodes.limit)}</span>
              </div>
            </div>

            <div className="usage-card-footer">
              <span className="remaining">
                {usage.nodes.remaining.toLocaleString()} {t('usageDashboard.remaining')}
              </span>
            </div>
          </div>

          {/* Stockage */}
          <div className="usage-card storage">
            <div className="usage-card-header">
              <div className="usage-card-icon">💾</div>
              <div className="usage-card-title">
                <h4>{t('usageDashboard.storage')}</h4>
                <span className="usage-card-subtitle">{t('usageDashboard.storedData')}</span>
              </div>
              <div className={`usage-card-percentage ${
                usage.storage.percentage >= 90 ? 'critical' : 
                usage.storage.percentage >= 80 ? 'warning' : 'normal'
              }`}>
                {usage.storage.percentage}%
              </div>
            </div>
            
            <div className="usage-card-progress">
              <div className="usage-progress-bar">
                <div 
                  className={`usage-progress-fill ${
                    usage.storage.percentage >= 90 ? 'critical' : 
                    usage.storage.percentage >= 80 ? 'warning' : 'normal'
                  }`}
                  style={{ width: `${Math.min(100, usage.storage.percentage)}%` }}
                ></div>
              </div>
              <div className="usage-numbers">
                <span className="used">{typeof usage.storage.used === 'number' ? usage.storage.used.toFixed(2) + ' GB' : usage.storage.used}</span>
                <span className="separator">/</span>
                <span className="limit">{formatStorage(usage.storage.limit)}</span>
              </div>
            </div>

            <div className="usage-card-footer">
              <span className="remaining">
                {usage.storage.remaining.toFixed(2)} GB {t('usageDashboard.remaining')}
              </span>
            </div>
          </div>

          {/* Utilisateurs */}
          <div className="usage-card users">
            <div className="usage-card-header">
              <div className="usage-card-icon">👥</div>
              <div className="usage-card-title">
                <h4>{t('usageDashboard.users')}</h4>
                <span className="usage-card-subtitle">{t('usageDashboard.team')}</span>
              </div>
              <div className={`usage-card-percentage ${
                usage.users.percentage >= 90 ? 'critical' : 
                usage.users.percentage >= 80 ? 'warning' : 'normal'
              }`}>
                {usage.users.percentage}%
              </div>
            </div>
            
            <div className="usage-card-progress">
              <div className="usage-progress-bar">
                <div 
                  className={`usage-progress-fill ${
                    usage.users.percentage >= 90 ? 'critical' : 
                    usage.users.percentage >= 80 ? 'warning' : 'normal'
                  }`}
                  style={{ width: `${Math.min(100, usage.users.percentage)}%` }}
                ></div>
              </div>
            <div className="usage-numbers">
                <span className="used">{usage.users.used}</span>
                <span className="separator">/</span>
                <span className="limit">{formatLimit(usage.users.limit)}</span>
              </div>
            </div>

            <div className="usage-card-footer">
              <span className="remaining">
                {usage.users.remaining} {t('usageDashboard.spotsRemaining')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="quick-actions">
        <h3>⚡ {t('usageDashboard.quickActions')}</h3>
        <div className="quick-actions-grid">
          <button 
            className="quick-action-btn upgrade"
            onClick={handleUpgradeRequest}
          >
            <span className="quick-action-icon">⬆️</span>
            <span className="quick-action-text">{t('usageDashboard.changePlan')}</span>
          </button>
          
          <button 
            className="quick-action-btn credits"
            onClick={() => setShowCreditModal(true)}
          >
            <span className="quick-action-icon">💳</span>
            <span className="quick-action-text">{t('usageDashboard.buyCredits')}</span>
          </button>
          
          <button 
            className="quick-action-btn support"
            onClick={() => window.open('mailto:nodemented.contact@gmail.com', '_blank')}
          >
            <span className="quick-action-icon">🎧</span>
            <span className="quick-action-text">{t('usageDashboard.contactSupport')}</span>
          </button>
          
          <button 
            className="quick-action-btn billing"
            onClick={() => window.location.href = '/billing'}
          >
            <span className="quick-action-icon">📄</span>
            <span className="quick-action-text">{t('usageDashboard.billingHistory')}</span>
          </button>
        </div>
      </div>

      {/* Modal d'achat de crédits */}
      <CreditPurchaseModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        currentUsage={usage.apiCalls}
        onPurchaseSuccess={handlePurchaseSuccess}
      />
    </div>
  );
};

export default UsageDashboard;
